import React, { useState } from 'react';
import { 
  Activity,
  Clock,
  AlertTriangle,
  User,
  Search,
  Users,
  CreditCard,
  UserCheck,
  Building2,
  GraduationCap,
  Settings,
  RefreshCw
} from 'lucide-react';
import PageContainer from '../components/PageContainer';
import {
  Button,
  DataToolbar,
  Input,
  EmptyState,
  ResponsiveDataView,
  SectionPanel,
  StatCard,
  StatusBadge,
} from '../components/ui';

const Activities = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('today');

  // Mock activities data - in real app, this would come from API
  const activities = [
    {
      id: 1,
      type: 'attendance',
      action: 'Điểm danh',
      details: 'Lớp Toán 10A1 - 28/30 học sinh có mặt',
      user: 'Cô Lan (Trợ giảng)',
      timestamp: '2024-01-15T09:20:00',
      status: 'completed',
      icon: UserCheck,
      color: 'bg-action-600',
      campus: 'Cơ sở Quận 1',
      additionalInfo: 'Vắng mặt: Nguyễn Văn A, Trần Thị B'
    },
    {
      id: 2,
      type: 'payment',
      action: 'Thanh toán học phí',
      details: 'Học phí tháng 6 - Nguyễn Văn C - 2,500,000 VND',
      user: 'Thu ngân',
      timestamp: '2024-01-15T08:45:00',
      status: 'completed',
      icon: CreditCard,
      color: 'bg-warning-600',
      campus: 'Cơ sở Quận 7',
      additionalInfo: 'Phương thức: Chuyển khoản'
    },
    {
      id: 3,
      type: 'registration',
      action: 'Đăng ký học sinh mới',
      details: 'Học sinh Lê Thị D - Lớp Anh 12B1',
      user: 'Admin',
      timestamp: '2024-01-15T07:30:00',
      status: 'pending',
      icon: Users,
      color: 'bg-academy-600',
      campus: 'Cơ sở Thủ Đức',
      additionalInfo: 'Chờ duyệt hồ sơ'
    },
    {
      id: 4,
      type: 'class',
      action: 'Tạo lớp học mới',
      details: 'Lớp Hóa 11A3 - Giáo viên: Thầy Minh',
      user: 'Quản lý cơ sở',
      timestamp: '2024-01-14T16:15:00',
      status: 'completed',
      icon: GraduationCap,
      color: 'bg-academy-700',
      campus: 'Cơ sở Bình Thạnh',
      additionalInfo: 'Sức chứa: 25 học sinh'
    },
    {
      id: 5,
      type: 'user',
      action: 'Thêm giáo viên mới',
      details: 'Giáo viên Trần Thị E - Môn Văn',
      user: 'Admin',
      timestamp: '2024-01-14T14:20:00',
      status: 'completed',
      icon: User,
      color: 'bg-academy-500',
      campus: 'Cơ sở Quận 1',
      additionalInfo: 'Kinh nghiệm: 5 năm'
    },
    {
      id: 6,
      type: 'system',
      action: 'Cập nhật hệ thống',
      details: 'Nâng cấp phiên bản 2.1.0',
      user: 'System Admin',
      timestamp: '2024-01-14T02:00:00',
      status: 'completed',
      icon: Settings,
      color: 'bg-ink-500',
      campus: 'Tất cả cơ sở',
      additionalInfo: 'Thời gian bảo trì: 30 phút'
    },
    {
      id: 7,
      type: 'alert',
      action: 'Cảnh báo học phí',
      details: 'Học sinh Phạm Văn F - Quá hạn 5 ngày',
      user: 'Hệ thống',
      timestamp: '2024-01-13T10:00:00',
      status: 'warning',
      icon: AlertTriangle,
      color: 'bg-danger-600',
      campus: 'Cơ sở Quận 7',
      additionalInfo: 'Số tiền: 3,000,000 VND'
    },
    {
      id: 8,
      type: 'campus',
      action: 'Thêm cơ sở mới',
      details: 'Cơ sở Gò Vấp - Quản lý: Bà Hoa',
      user: 'Super Admin',
      timestamp: '2024-01-12T09:30:00',
      status: 'completed',
      icon: Building2,
      color: 'bg-action-700',
      campus: 'Cơ sở Gò Vấp',
      additionalInfo: 'Địa chỉ: 123 Phạm Văn Đồng'
    }
  ];

  const filterOptions = [
    { value: 'all', label: 'Tất cả hoạt động' },
    { value: 'attendance', label: 'Điểm danh' },
    { value: 'payment', label: 'Thanh toán' },
    { value: 'registration', label: 'Đăng ký' },
    { value: 'class', label: 'Lớp học' },
    { value: 'user', label: 'Người dùng' },
    { value: 'system', label: 'Hệ thống' },
    { value: 'alert', label: 'Cảnh báo' },
    { value: 'campus', label: 'Cơ sở' }
  ];

  const dateRangeOptions = [
    { value: 'today', label: 'Hôm nay' },
    { value: 'yesterday', label: 'Hôm qua' },
    { value: 'week', label: '7 ngày qua' },
    { value: 'month', label: '30 ngày qua' },
    { value: 'all', label: 'Tất cả' }
  ];

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.campus.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = selectedFilter === 'all' || activity.type === selectedFilter;
    
    // Simple date filtering (in real app, this would be more sophisticated)
    const activityDate = new Date(activity.timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    let matchesDate = true;
    if (selectedDateRange === 'today') {
      matchesDate = activityDate.toDateString() === today.toDateString();
    } else if (selectedDateRange === 'yesterday') {
      matchesDate = activityDate.toDateString() === yesterday.toDateString();
    } else if (selectedDateRange === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      matchesDate = activityDate >= weekAgo;
    } else if (selectedDateRange === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);
      matchesDate = activityDate >= monthAgo;
    }
    
    return matchesSearch && matchesFilter && matchesDate;
  });

  const getStatusTone = (status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'warning':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Hoàn thành';
      case 'pending':
        return 'Đang xử lý';
      case 'warning':
        return 'Cảnh báo';
      default:
        return 'Không xác định';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      time: date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }),
      date: date.toLocaleDateString('vi-VN')
    };
  };

  return (
    <PageContainer
      title="Nhật ký hoạt động"
      subtitle="Theo dõi tất cả hoạt động vận hành trong hệ thống."
      actions={(
        <>
          <Button icon={<RefreshCw className="h-4 w-4" />} onClick={() => window.location.reload()}>
            Làm mới
          </Button>
        </>
      )}
    >

      {/* Filters */}
      <DataToolbar
          className="sm:items-center"
          controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center"
      >
          <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Tìm hoạt động…"
              leftIcon={<Search className="h-4 w-4" />}
              aria-label="Tìm kiếm hoạt động"
              className="min-h-9 lg:w-72"
          />
          {/* Activity Type Filter */}
          <select
              aria-label="Lọc theo loại hoạt động"
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
            >
              {filterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

          {/* Date Range Filter */}
          <select
              aria-label="Lọc theo khoảng thời gian"
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
              className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
            >
              {dateRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>


          {/* Search */}
      </DataToolbar>

      {/* Activities List */}
      <SectionPanel className="overflow-hidden max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">
        
          {filteredActivities.length > 0 ? (
            <ResponsiveDataView
              desktopLabel="Danh sách hoạt động"
              mobileLabel="Thẻ hoạt động"
              desktop={(
                <div className="divide-y divide-ink-200">
                  {filteredActivities.map((activity) => {
                    const timeInfo = formatTime(activity.timestamp);
                    return (
                      <div
                        key={activity.id}
                        className="flex items-start gap-4 p-4 transition-colors duration-200 hover:bg-ink-50 sm:px-5"
                      >

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-ink-900">{activity.action}</h3>
                              <p className="mt-1 text-sm text-ink-600">{activity.details}</p>
                              {activity.additionalInfo && (
                                <p className="mt-1 text-xs text-ink-500">{activity.additionalInfo}</p>
                              )}
                            </div>

                            <StatusBadge tone={getStatusTone(activity.status)}>
                              {getStatusLabel(activity.status)}
                            </StatusBadge>
                          </div>

                          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-500">
                            <div className="flex flex-wrap items-center gap-3">
                              <span className="inline-flex items-center gap-1">
                                <User className="h-3.5 w-3.5" />
                                {activity.user}
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <Building2 className="h-3.5 w-3.5" />
                                {activity.campus}
                              </span>
                            </div>

                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {timeInfo.time} - {timeInfo.date}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              mobile={(
                <div className="grid gap-3">
                  {filteredActivities.map((activity) => {
                    const timeInfo = formatTime(activity.timestamp);
                    return (
                      <article key={activity.id} className="rounded-lg border border-ink-200 bg-white p-4">
                        <div className="flex items-start gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="text-sm font-semibold text-ink-900">{activity.action}</h3>
                              <StatusBadge tone={getStatusTone(activity.status)} className="shrink-0">
                                {getStatusLabel(activity.status)}
                              </StatusBadge>
                            </div>
                            <p className="mt-1 text-sm text-ink-600">{activity.details}</p>
                            {activity.additionalInfo && (
                              <p className="mt-1 text-xs text-ink-500">{activity.additionalInfo}</p>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 grid gap-1 border-t border-ink-200 pt-3 text-xs text-ink-500">
                          <span>{activity.user}</span>
                          <span>{activity.campus}</span>
                          <span>{timeInfo.time} - {timeInfo.date}</span>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            />
          ) : (
            <EmptyState
              title="Không tìm thấy hoạt động nào"
              description="Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm."
            />
          )}
      </SectionPanel>
    </PageContainer>
  );
};

export default Activities;
